import React, { useState } from 'react';
import api from '../api';

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const IconSun = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const IconMoon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

function Login({ onLogin, darkMode, toggleDarkMode }) {
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);

  // TOTP state — mode is 'login' | 'setup' | 'verify'
  var [mode, setMode] = useState('login');
  // Short-lived challenge token from step 1; proves the password step to step 2
  var [totpToken, setTotpToken] = useState(null);
  var [qrCode, setQrCode] = useState('');
  var [secret, setSecret] = useState('');
  var [totpCode, setTotpCode] = useState('');

  // Step 1: Email + Password
  var handleLogin = async function(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      var response = await api.post('/auth/login', {
        email: email,
        password: password
      });

      if (response.data.totp_setup_required) {
        setMode('setup');
        setTotpToken(response.data.totp_token);
        setQrCode(response.data.qr_code);
        setSecret(response.data.secret);
      } else if (response.data.totp_required) {
        setMode('verify');
        setTotpToken(response.data.totp_token);
      } else {
        onLogin(response.data.token, response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Confirm TOTP setup (first time only)
  var handleSetupVerify = async function(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      var response = await api.post('/auth/verify-totp-setup', {
        totp_token: totpToken,
        token: totpCode
      });
      if (response.data.success) {
        onLogin(response.data.token, response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verify TOTP (every login after setup)
  var handleVerify = async function(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      var response = await api.post('/auth/verify-totp', {
        totp_token: totpToken,
        token: totpCode
      });
      if (response.data.success) {
        onLogin(response.data.token, response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Allow only digits, max 6
  var handleCodeChange = function(e) {
    setTotpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
  };

  var backToLogin = function() {
    setMode('login');
    setTotpToken(null);
    setTotpCode('');
    setError('');
  };

  return (
    <div className="console-auth">
      {/* ── Top bar ── */}
      <header className="console-topbar">
        <div className="console-brand">
          <div className="console-brand-mark"><IconShield /></div>
          <h2>Shaheen</h2>
        </div>
        <div className="console-channel">
          <span className="pulse-dot ok" />
          <span>Monitoring active</span>
        </div>
      </header>

      {/* ── Center panel ── */}
      <main className="console-center">
        <div className="console-panel rise" style={{ '--i': 0 }}>
          <div className="console-panel-head">
            <span className="pulse-dot ok" aria-hidden="true" style={{ animation: 'none' }} />
            <h1>
              {mode === 'login' && 'Operator sign-in'}
              {mode === 'setup' && 'Two-factor setup'}
              {mode === 'verify' && 'Two-factor verification'}
            </h1>
          </div>

          <div className="console-panel-body">
            <p className="console-panel-sub">
              {mode === 'login' && 'Enter your credentials to access the monitoring console.'}
              {mode === 'setup' && 'Protect your account with an authenticator app before continuing.'}
              {mode === 'verify' && 'Enter the current code from your authenticator app.'}
            </p>

            {error && <div className="alert alert-error">{error}</div>}

            {mode === 'login' && (
              /* ── Step 1: Email + Password ── */
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label>Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={function(e) { setEmail(e.target.value); }}
                    required
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={function(e) { setPassword(e.target.value); }}
                    required
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>

                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>
            )}

            {mode === 'setup' && (
              /* ── Step 2: TOTP Setup (first login only) ── */
              <form onSubmit={handleSetupVerify}>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16, textAlign: 'left', lineHeight: 1.6 }}>
                    1. Install <strong>Google Authenticator</strong> (or any TOTP app) on your phone<br/>
                    2. Scan this QR code<br/>
                    3. Enter the 6-digit code it shows
                  </p>
                  {qrCode && (
                    <img src={qrCode} alt="TOTP QR Code" style={{
                      width: 180, height: 180, borderRadius: 'var(--radius-sm)',
                      background: 'white', padding: 8, margin: '0 auto', display: 'block',
                      border: '1px solid var(--border)'
                    }} />
                  )}
                  <div style={{ marginTop: 14 }}>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Can't scan? Enter this code manually:
                    </p>
                    <code style={{
                      fontSize: 12, color: 'var(--primary)',
                      fontFamily: 'var(--font-mono, monospace)',
                      wordBreak: 'break-all', display: 'block', marginTop: 4
                    }}>{secret}</code>
                  </div>
                </div>

                <div className="form-group">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={handleCodeChange}
                    required
                    placeholder="000000"
                    maxLength="6"
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    style={{
                      textAlign: 'center',
                      fontSize: 26,
                      fontWeight: 600,
                      letterSpacing: 12,
                      fontFamily: 'var(--font-mono, monospace)'
                    }}
                  />
                </div>

                <button type="submit" className="btn btn-primary" disabled={loading || totpCode.length !== 6}>
                  {loading ? 'Verifying…' : 'Verify & Continue'}
                </button>

                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <button type="button" onClick={backToLogin} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: 12, fontFamily: 'var(--font)'
                  }}>← Back to login</button>
                </div>
              </form>
            )}

            {mode === 'verify' && (
              /* ── Step 3: TOTP Verify (every login after setup) ── */
              <form onSubmit={handleVerify}>
                <div className="form-group">
                  <label>Authentication Code</label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={handleCodeChange}
                    required
                    placeholder="000000"
                    maxLength="6"
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    style={{
                      textAlign: 'center',
                      fontSize: 26,
                      fontWeight: 600,
                      letterSpacing: 12,
                      fontFamily: 'var(--font-mono, monospace)'
                    }}
                  />
                  <div className="form-hint" style={{ textAlign: 'center' }}>
                    Code refreshes every 30 seconds
                  </div>
                </div>

                <button type="submit" className="btn btn-primary" disabled={loading || totpCode.length !== 6}>
                  {loading ? 'Verifying…' : 'Verify Code'}
                </button>

                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <button type="button" onClick={backToLogin} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: 12, fontFamily: 'var(--font)'
                  }}>← Back to login</button>
                </div>
              </form>
            )}
          </div>
        </div>

        <button className="auth-theme-btn" onClick={toggleDarkMode}>
          {darkMode ? <IconSun /> : <IconMoon />}
          {darkMode ? 'Switch to Day Shift' : 'Switch to Night Shift'}
        </button>
      </main>

      {/* ── Foot ── */}
      <footer className="console-foot">
        Uptime and DNS record monitoring
      </footer>
    </div>
  );
}

export default Login;
